import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import MainLayout from "../components/layout/MainLayout";
import DischargeSummaryList from "../pages/Phase-07/DischargeSummary/DischargeSummaryList";
import Breadcrumbs from "../components/common/Breadcrumbs";
import { 
  fetchDischargeSummaries, 
  deleteDischargeSummary,
  clearError 
} from "../store/slices/dischargeSummarySlice";
import Swal from "sweetalert2";

const DischargeSummaries = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    // Redux state
    const { 
        dischargeSummaries, 
        isLoading, 
        error 
    } = useSelector((state) => state.dischargeSummary);

    // Fetch discharge summaries on component mount
    useEffect(() => {
        dispatch(fetchDischargeSummaries());
    }, [dispatch]);

    // Clear errors on unmount
    useEffect(() => {
        return () => dispatch(clearError());
    }, [dispatch]);

    // Handle delete with confirmation
    const handleDelete = async (id) => {
        const result = await Swal.fire({
            title: "Are you sure?",
            text: "This Discharge Summary record will be permanently deleted!",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: "Yes, delete it!",
        });

        if (result.isConfirmed) {
            try {
                await dispatch(deleteDischargeSummary(id)).unwrap();
                Swal.fire("Deleted!", "The record has been removed.", "success");
            } catch (error) {
                Swal.fire("Error!", error || "Failed to delete record.", "error");
            }
        }
    };

    // Handle edit navigation
    const handleEdit = (id) => {
        navigate(`/phase07/discharge-summary/edit/${id}`);
    };

    // Handle add navigation
    const handleAdd = () => {
        navigate("/phase07/discharge-summary/add");
    };

    // Transform data for the list component
    const transformedRecords = dischargeSummaries.map(record => ({
        id: record.id,
        client_name: record.client_name || "N/A",
        program: record.program || "N/A",
        admission_date: record.admission_date || "N/A",
        discharge_date: record.discharge_date || "N/A",
        reason: record.discharge_reason || "N/A",
        reviewed_by: record.creator?.name || "N/A",
        // Add any other fields you want to display
    }));

    // Loading state
    if (isLoading) {
        return (
            <MainLayout>
                <div className="container-xxl flex-grow-1 container-p-y">
                    <div className="card">
                        <div className="card-body text-center py-5">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                            <p className="mt-3 text-muted">Loading discharge summaries...</p>
                        </div>
                    </div>
                </div>
            </MainLayout>
        );
    }

    // Error state
    if (error) {
        return (
            <MainLayout>
                <div className="container-xxl flex-grow-1 container-p-y">
                    <div className="card">
                        <div className="card-body text-center py-5">
                            <i className="ri-error-warning-line fs-1 text-danger d-block mb-3"></i>
                            <h5 className="text-danger">Error Loading Data</h5>
                            <p className="text-muted">{error}</p>
                            <button
                                className="btn btn-outline-primary btn-sm"
                                onClick={() => dispatch(fetchDischargeSummaries())}
                            >
                                Retry
                            </button>
                        </div>
                    </div>
                </div>
            </MainLayout>
        );
    }

    return (
        <MainLayout>
            <div className="container-xxl flex-grow-1 container-p-y">
                <Breadcrumbs />
                <div className="card">
                    <DischargeSummaryList
                        records={transformedRecords}
                        onAdd={handleAdd}
                        onEdit={handleEdit}
                        onDelete={handleDelete}
                        isLoading={isLoading}
                    />
                </div>
            </div>
        </MainLayout>
    );
};

export default DischargeSummaries;